import A11yDialog from "a11y-dialog";
import { setCookie } from "../utils/helpers.js";

// Tracks instantiated dialogs by DOM element so repeat inits are no-ops.
const dialogInstances = new WeakMap();
// Ensures global event listeners are attached only once.
let dialogsBootstrapped = false;

function initDialogInstances(root = document) {
	// Initialize dialogs only inside the provided root (or full document by default).
	const elements = root instanceof Element
		? (root.matches("[data-a11y-dialog]")
			? [root, ...root.querySelectorAll("[data-a11y-dialog]")]
			: [...root.querySelectorAll("[data-a11y-dialog]")])
		: [...document.querySelectorAll("[data-a11y-dialog]")];

	elements.forEach((dialogElement) => {
		if (!dialogInstances.has(dialogElement)) {
			dialogInstances.set(dialogElement, new A11yDialog(dialogElement));
		}
	});
}

function destroyDialogInstances(root) {
	// before:swap can provide non-element targets in edge cases; ignore those.
	if (!(root instanceof Element)) {
		return;
	}

	const elements = root.matches("[data-a11y-dialog]")
		? [root, ...root.querySelectorAll("[data-a11y-dialog]")]
		: [...root.querySelectorAll("[data-a11y-dialog]")];

	elements.forEach((dialogElement) => {
		const instance = dialogInstances.get(dialogElement);
		if (instance) {
			instance.destroy();
			dialogInstances.delete(dialogElement);
		}
	});
}

/**
 * Initialize all a11y-dialog instances
 */
export function initDialogs() {
	// Make A11yDialog globally available
	window.A11yDialog = A11yDialog;
	initDialogInstances();

	if (dialogsBootstrapped) {
		return;
	}

	dialogsBootstrapped = true;

	// Keep anchor dialog triggers from navigating when JS is active.
	document.addEventListener("click", function (event) {
		const trigger = event.target.closest("a[data-a11y-dialog-show]");
		if (!trigger) {
			return;
		}

		event.preventDefault();
	});

	

	document.addEventListener("htmx:before:swap", function (event) {
		// Remove instances tied to outgoing DOM before HTMX replaces that region.
		destroyDialogInstances(event.detail?.target || event.target);
	});

	document.addEventListener("htmx:after:swap", function (event) {
		// Initialize dialogs in newly swapped markup.
		initDialogInstances(event.detail?.target || event.target);
	});

	document.addEventListener("htmx:after:settle", function (event) {
		// Settle pass catches delayed/transitioned content in the same target.
		initDialogInstances(event.detail?.target || event.target);
	});
}

/**
 * Add History API support to dialogs with data-dialog-history attribute
 */
export function initDialogHistory() {
	const historyDialogs = document.querySelectorAll(
		"[data-a11y-dialog][data-dialog-history]",
	);

	historyDialogs.forEach((dialogElement) => {
		const dialogId = dialogElement.id;
		const paramKey =
			dialogElement.getAttribute("data-dialog-history") || "dialog";

		// Add dialog_id to URL on show
		dialogElement.addEventListener("show", function () {
			const url = new URL(window.location);
			url.searchParams.set(paramKey, dialogId);
			window.history.pushState({}, "", url);
		});

		// Remove dialog_id from URL on hide
		dialogElement.addEventListener("hide", function () {
			const url = new URL(window.location);
			url.searchParams.delete(paramKey);
			window.history.pushState({}, "", url);
		});
	});

	// Handle browser back/forward navigation
	window.addEventListener("popstate", function () {
		const url = new URL(window.location);

		// Close all dialogs with history support first
		historyDialogs.forEach((dialogElement) => {
			if (dialogElement.getAttribute("aria-hidden") === "false") {
				// Trigger click on close button to properly close the dialog
				const closeButton = dialogElement.querySelector(
					"[data-a11y-dialog-hide]",
				);
				if (closeButton) {
					closeButton.click();
				}
			}
		});

		// Check all possible parameter keys and open matching dialog
		historyDialogs.forEach((dialogElement) => {
			const paramKey =
				dialogElement.getAttribute("data-dialog-history") || "dialog";
			const dialogParam = url.searchParams.get(paramKey);

			if (dialogParam && dialogElement.id === dialogParam) {
				const openButton = document.querySelector(
					`[data-a11y-dialog-show="${dialogParam}"]`,
				);
				if (openButton) {
					openButton.click();
				}
			}
		});
	});

	// Open dialog on page load if specified in URL
	const url = new URL(window.location);

	historyDialogs.forEach((dialogElement) => {
		const paramKey =
			dialogElement.getAttribute("data-dialog-history") || "dialog";
		const dialogParam = url.searchParams.get(paramKey);

		if (dialogParam && dialogElement.id === dialogParam) {
			const openButton = document.querySelector(
				`[data-a11y-dialog-show="${dialogParam}"]`,
			);
			if (openButton) {
				openButton.click();
			}
		}
	});
}

/**
 * Initialize popup dialogs with delay
 */
export function initPopupDialogs() {
	const popupDialogs = document.querySelectorAll("[data-dialog-delay]");

	popupDialogs.forEach((dialogElement) => {
		const dialogDelay = dialogElement.dataset.dialogDelay;
		const dialogId = dialogElement.dataset.dialogId;
		const popupCookie = "STYXKEY-popup-" + dialogId;

		// Check if dialog was already dismissed
		if (document.cookie.indexOf(popupCookie) !== -1) {
			return;
		}

		let dialog = dialogInstances.get(dialogElement);
		if (!dialog) {
			dialog = new A11yDialog(dialogElement);
			dialogInstances.set(dialogElement, dialog);
		}

		// Set cookie when dialog is closed
		dialog.on("hide", function () {
			setCookie(popupCookie, true);
		});

		// Show dialog after delay, during an idle period for better UX
		const delay = parseInt(dialogDelay) * 1000;
		setTimeout(() => {
			// Wait for browser idle time before showing popup
			// This prevents interrupting animations or user interactions
			if (window.requestIdleCallback) {
				requestIdleCallback(
					() => {
						dialog.show();
					},
					{ timeout: 1000 }, // Show within 1s even if browser stays busy
				);
			} else {
				// Fallback for browsers without requestIdleCallback support
				dialog.show();
			}
		}, delay);
	});
}
